// frontend/src/pages/crm/Accounts.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { accountAPI } from '../../utils/crmApi';
import { useBranding } from '../../contexts/BrandingContext';
import { useCRM } from '../../contexts/CRMContext';
import { PERMISSIONS } from '../../utils/permissions';
import { hasPermission } from '../../utils/permissions';
import toast from 'react-hot-toast';
import {
    BuildingOfficeIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    EnvelopeIcon,
    PhoneIcon
} from '@heroicons/react/24/outline';
import { AccountCardGrid, AccountStatsCard } from '../ui/enhanced-account-card';

const Accounts = () => {
    const { branding } = useBranding();
    const { user, currentAgency } = useCRM();
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20 });

    useEffect(() => {
        loadAccounts();
    }, [search, statusFilter, pagination.page]);

    const loadAccounts = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(search && { search }),
                ...(statusFilter && { status: statusFilter })
            };

            const response = await accountAPI.getAll(params);
            if (response.data.success) {
                setAccounts(response.data.data);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
        } catch (error) {
            console.error('Failed to load accounts:', error);
            toast.error('Failed to load accounts');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            prospect: `text-white`,
            active: `text-white`,
            inactive: 'bg-muted text-foreground',
            archived: 'bg-muted text-foreground'
        };
        
        const getStatusBgColor = (status) => {
            switch (status) {
                case 'prospect': return branding.primaryColor;
                case 'active': return branding.secondaryColor;
                default: return '#6B7280';
            }
        };
        
        return {
            className: colors[status] || colors.prospect,
            style: status === 'prospect' || status === 'active' ? { backgroundColor: getStatusBgColor(status) } : {}
        };
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Accounts</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Manage company accounts and their contacts
                    </p>
                </div>
                {hasPermission(user, PERMISSIONS.CREATE_ACCOUNT, currentAgency?._id) && (
                    <Link
                        to="/accounts/new"
                        className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ 
                            backgroundColor: branding.primaryColor,
                            '--tw-ring-color': branding.primaryColor
                        }}
                    >
                        <BuildingOfficeIcon className="h-5 w-5 mr-2" />
                        Add Account
                    </Link>
                )}
            </div>

            {/* Filters */}
            <div className="bg-card rounded-xl shadow-sm border border-border p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Search */}
                    <div className="col-span-2">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Search accounts by name..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                style={{ '--tw-ring-color': branding.primaryColor }}
                            />
                        </div>
                    </div>

                    {/* Status Filter */}
                    <div className="relative">
                        <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring appearance-none"
                        >
                            <option value="">All Statuses</option>
                            <option value="prospect">Prospect</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="archived">Archived</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Accounts Grid */}
                {loading ? (
                <div className="flex items-center justify-center h-64">
                        <div 
                            className="animate-spin rounded-full h-12 w-12 border-b-2"
                            style={{ borderBottomColor: branding?.primaryColor || '#3b82f6' }}
                        ></div>
                    </div>
            ) : (
                <AccountCardGrid
                    accounts={accounts}
                    emptyMessage="No accounts match your search criteria"
                />
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="bg-background px-4 py-3 flex items-center justify-between border-t border-border sm:px-6 rounded-lg">
                    <div className="flex-1 flex justify-between sm:hidden">
                        <button
                            onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                            disabled={pagination.page === 1}
                            className="relative inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md text-foreground bg-background hover:bg-muted disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                            disabled={pagination.page === pagination.pages}
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md text-foreground bg-background hover:bg-muted disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-muted-foreground">
                                Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                                <span className="font-medium">{pagination.pages}</span>
                            </p>
                        </div>
                        <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                <button
                                    onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                                    disabled={pagination.page === 1}
                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-border bg-background text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                                    disabled={pagination.page === pagination.pages}
                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-border bg-background text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Accounts;





